import React from "react";
import {
    DotLegalButton,
    DotLegalCheckbox,
    DotLegalEmptyState,
    DotLegalInformationTooltip,
    DotLegalTable,
    DotLegalTooltip,
} from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import FilterBar from "@common/components/filterBar/FilterBar";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import EmptyState from "@common/svgs/emptyState.svg?react";
import { InternalAssessmentGroupDraft } from "../../InternalAssessmentGroups.types";
import { SelectableAssignee } from "../../SelectableAssignee.types";
import { getSelectableAssigneeHeaders, SelectableAssigneeCells } from "../../selectableAssigneeTable/SelectableAssigneeColumns";
import useInternalAssessmentGroupAssigneeSelector from "./InternalAssessmentGroupAssigneeSelector.hooks";
import SendAssessmentsDialog from "../sendAssessmentsDialog/SendAssessmentsDialog";
import AddEditExternalUserDialog from "../../../externalUser/addEditExternalUserDialog/AddEditExternalUserDialog";
import { WhiteLockIcon } from "@common/icons";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import PlanDialogButtonWrapper from "../../../plan/planDialogWrapper/planDialogWrapper";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface IInternalAssessmentGroupAssigneeSelectorProps {
    internalAssessmentGroupId: string;
    internalAssessmentGroupName: string;
    rows: Array<SelectableAssignee>;
    selectedAssignees: Array<InternalAssessmentGroupDraft>;
    // Reached from the group's "add recipients" menu item rather than from creating a draft, so the group already
    // exists to go back to.
    isAddingToSentGroup: boolean;
    isDeadlineOverdue: boolean;
    isLoading: boolean;
    onSelectedAssigneesChange: (selectedAssignees: Array<InternalAssessmentGroupDraft>) => Promise<void>;
    onRecipientAdded: () => Promise<void>;
}

function InternalAssessmentGroupAssigneeSelector(props: IInternalAssessmentGroupAssigneeSelectorProps) {
    const { translateString } = useTranslation();
    const { vendorManagementFeatures } = usePlanContext();
    const {
        rows,
        isSelected,
        onCheckBoxClick,
        onSelectAll,
        headerCheckBoxState,
        getFilterDataOptions,
        onFilterChange,
        searchString,
        setSearchString,
        selectedAssigneeRows,
        showSendDialog,
        setShowSendDialog,
        canCreateRecipients,
        showAddRecipientDialog,
        setShowAddRecipientDialog,
        onRecipientCreated,
        onCancelClick,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useInternalAssessmentGroupAssigneeSelector(props);

    const hasInternalAssessmentGroups = vendorManagementFeatures.internalAssessmentGroups;
    const upgradeText = translateString("upgradeVendorManagementPlan");
    // Both footer buttons are blocked by the same overdue deadline and fixed by the same extension, so they say
    // the same thing about it.
    const deadlineOverdueTooltip = props.isDeadlineOverdue ? translateString("internalAssessmentGroupDeadlineIsOverdue") : undefined;
    const sendButtonTooltip = !hasInternalAssessmentGroups ? upgradeText : deadlineOverdueTooltip;

    function getFilters() {
        return (
            <Box sx={{ display: "flex", flex: "1 1 auto", alignItems: "center", gap: 1 }}>
                <FilterBar
                    isLoading={props.isLoading}
                    filterData={getFilterDataOptions()}
                    fetchFilterData
                    onMenuClose={(options) => onFilterChange(options)}
                    onRemove={(options) => onFilterChange(options)}
                    pinnedFilterIds={pinnedFilterIds}
                    onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                    searchValue={searchString}
                    searchLabel={translateString("search")}
                    onSearchChange={(value) => setSearchString(value)}
                />
                <DotLegalInformationTooltip text={translateString("businessAreaFilterHidesContacts")} />
            </Box>
        );
    }

    function getAddRecipientButton() {
        if (!canCreateRecipients) {
            return undefined;
        }

        return (
            <DotLegalButton
                buttonType="secondary"
                size="medium"
                disabled={props.isDeadlineOverdue}
                toolTip={deadlineOverdueTooltip}
                onClick={() => setShowAddRecipientDialog(true)}
            >
                {translateString("addRecipient")}
            </DotLegalButton>
        );
    }

    return (
        <React.Fragment>
            <TablePagePaper>
                {/* Loading counts as having rows so the table shows its skeleton. Otherwise the first paint claims
                    there is no one to send to, on a page whose whole purpose is picking recipients. */}
                {props.isLoading || props.rows.length > 0 ? (
                    <>
                        <ListPageToolbar leftContent={getFilters()} />
                        <DotLegalTable
                            internalScroll
                            headers={getSelectableAssigneeHeaders(translateString)}
                            rowsPerPage={30}
                            defaultOrderBy={"name"}
                            defaultOrder={"asc"}
                            isLoading={props.isLoading}
                            searchString={searchString}
                            paginationLabelOf={translateString("labelOf")}
                            emptyText={translateString("noData")}
                            data={rows}
                            onSelectAllChanged={onSelectAll}
                            headerCheckBoxState={headerCheckBoxState}
                            renderRow={(row) => (
                                <TableRow key={`${row.assigneeType}-${row.id}`}>
                                    <TableCell onClick={(event) => event.stopPropagation()} sx={{ padding: "0 0 0 4px" }}>
                                        {/* The span is what the tooltip hangs on: a disabled checkbox fires no hover events of its own.
                                            Without it the only clue why nothing can be selected is hovering the Send button. */}
                                        <DotLegalTooltip text={deadlineOverdueTooltip}>
                                            <span>
                                                <DotLegalCheckbox
                                                    onChange={() => onCheckBoxClick(row)}
                                                    checked={isSelected(row)}
                                                    disabled={props.isDeadlineOverdue}
                                                />
                                            </span>
                                        </DotLegalTooltip>
                                    </TableCell>
                                    <SelectableAssigneeCells row={row} translateString={translateString} />
                                </TableRow>
                            )}
                        />
                    </>
                ) : (
                    <DotLegalEmptyState icon={<EmptyState />} text={translateString("noRecipientsToAddToInternalAssessmentGroup")} />
                )}

                {/* Outside the empty-state branch above: with no recipient left to select, creating one is the
                    only thing left to do here, so that is exactly when the button must stay reachable. */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                    {/* Adding recipients to a group that is already sent is a page of its own reached from the group,
                        so it needs a way back that does not depend on sending anything. */}
                    {props.isAddingToSentGroup && (
                        <DotLegalButton buttonType="secondary" size="medium" onClick={onCancelClick}>
                            {translateString("cancel")}
                        </DotLegalButton>
                    )}

                    {getAddRecipientButton()}

                    <PlanDialogButtonWrapper enabled={!hasInternalAssessmentGroups} text={upgradeText}>
                        <DotLegalButton
                            buttonType="primary"
                            size="medium"
                            disabled={!hasInternalAssessmentGroups || props.selectedAssignees.length === 0 || props.isDeadlineOverdue}
                            endIcon={!hasInternalAssessmentGroups ? WhiteLockIcon : undefined}
                            toolTip={sendButtonTooltip}
                            onClick={() => setShowSendDialog(true)}
                        >
                            {translateString("sendAssessments")} {props.selectedAssignees.length > 0 && `(${props.selectedAssignees.length})`}
                        </DotLegalButton>
                    </PlanDialogButtonWrapper>
                </Box>
            </TablePagePaper>

            {showSendDialog && (
                <SendAssessmentsDialog
                    internalAssessmentGroupId={props.internalAssessmentGroupId}
                    internalAssessmentGroupName={props.internalAssessmentGroupName}
                    selectedAssignees={selectedAssigneeRows}
                    onDialogClose={() => setShowSendDialog(false)}
                />
            )}

            {showAddRecipientDialog && (
                <AddEditExternalUserDialog externalUser={undefined} onCloseDialog={() => setShowAddRecipientDialog(false)} onSave={onRecipientCreated} />
            )}
        </React.Fragment>
    );
}

export default InternalAssessmentGroupAssigneeSelector;
